import { z } from 'zod';
import { AiModelSchema, AiReasoningEffortSchema } from '../entities/organization/organization.schema';
import { DEFAULT_DECISION_MODEL } from './ai-decision-models';
import { APP_SLUG_PATTERN } from './app-slug';
import { AI_MODEL_CONFIGS } from './organization-defaults';
/**
 * Registry of the CORE platform AI consumers. Each feature has a default
 * tier (high/low/decision) which determines its fallback model when no
 * per-feature override is configured. Adding a new core AI consumer requires
 * adding its key here. Installed apps are NOT listed — they declare their
 * tier in their own manifest (`ai.text`) and are overridden per appId via
 * `appModelOverrides` in the platform AI settings.
 */ export const AI_FEATURE_KEYS = [
    'bliksy-chat',
    'bliksy-modify-subagent',
    'step-summary',
    'chat-title',
    'chat-summarizer',
    'decision-eval-judge'
];
export const AiFeatureKeySchema = z.enum(AI_FEATURE_KEYS);
import { AiConsumerTierSchema, AiTierSchema, isAiTier } from './ai-tier';
export { AiConsumerTierSchema, AiTierSchema, isAiTier };
export const AI_FEATURES = {
    'bliksy-chat': {
        defaultTier: 'high'
    },
    'bliksy-modify-subagent': {
        defaultTier: 'low'
    },
    'step-summary': {
        defaultTier: 'low'
    },
    'chat-title': {
        defaultTier: 'low'
    },
    'chat-summarizer': {
        defaultTier: 'low'
    },
    'decision-eval-judge': {
        defaultTier: 'decision'
    }
};
/**
 * A per-feature override accepts either a specific model or a tier. Storing
 * `'high' | 'low'` makes a feature *track* the tier — i.e. if the operator
 * later swaps the high model, every feature pinned to `'high'` follows along.
 */ export const AiFeatureOverrideSchema = z.union([
    AiModelSchema,
    AiTierSchema
]);
/** The `AppModelOverrideMap` key for an app, or for one of its model slots. */ export function appModelOverrideKey(appId, slot) {
    return slot ? `${appId}:${slot}` : appId;
}
/**
 * An `appId`, optionally suffixed with `:slot` — the validator counterpart of
 * `appModelOverrideKey`, composed from the same charset as slot names
 * (`APP_SLUG_PATTERN`). Constrained on the write path only: the server writes
 * the key into a Mongo dot-path (`appModelOverrides.<key>`), where a dot in
 * the key would nest the `$set` under a subdocument nobody asked for. One bad
 * key rejects the whole record, which is the intent — the stored
 * `PlatformAiSettingsSchema` stays unconstrained so existing documents keep
 * parsing.
 */ export const AppModelOverrideKeySchema = z.string().regex(new RegExp(`^${APP_SLUG_PATTERN}(:${APP_SLUG_PATTERN})?$`), 'App model override keys must be an appId, optionally suffixed with :slot');
/**
 * `undefined` for a stored value that no longer parses (e.g. a model retired
 * from the enum), so resolution falls through to the next level instead of
 * pinning the caller to something that cannot run. The server and the admin
 * UI both read `appModelOverrides` through this, so they agree on staleness.
 */ export function parseAiFeatureOverride(value) {
    const parsed = AiFeatureOverrideSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
/** `parseAiFeatureOverride`'s counterpart for a stored tier model. */ export function parseAiModel(value) {
    const parsed = AiModelSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
/** `parseAiFeatureOverride`'s counterpart for the stored reasoning efforts. */ export function parseAiReasoningEffort(value) {
    const parsed = AiReasoningEffortSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
export function modelRunsEffort(model, effort) {
    return AI_MODEL_CONFIGS[model].reasoningEfforts.includes(effort);
}
/**
 * The effort a call on `model` runs at: the requested one, else the model's
 * default. A request outside the model's range — a setting made for another
 * model — clamps to the nearest end instead of reaching the provider, which
 * would drop it silently.
 */ export function effectiveReasoningEffort(model, requested) {
    const { reasoningEfforts, defaultReasoningEffort } = AI_MODEL_CONFIGS[model];
    if (!requested) return defaultReasoningEffort;
    if (modelRunsEffort(model, requested)) return requested;
    const order = AiReasoningEffortSchema.options;
    return order.indexOf(requested) < order.indexOf(reasoningEfforts[0]) ? reasoningEfforts[0] : reasoningEfforts[reasoningEfforts.length - 1];
}
/**
 * Apply the override → tier-default fallback. Returns `undefined` if neither
 * an override nor the relevant tier is set in `settings`, letting the caller
 * decide on its own ultimate fallback (e.g. a constant). Shared by core
 * features (defaultTier from AI_FEATURES) and apps (defaultTier from the
 * manifest's `ai.text`) so the resolution rule lives in exactly one place.
 *
 * The effort travels with the model: a pinned model runs at
 * `reasoningEffort` (the feature's or app's own setting) or its default; a
 * tier-resolved model runs at its tier's effort — a row that follows a tier
 * has no effort of its own.
 */ export function resolveTierModel(override, defaultTier, settings, reasoningEffort) {
    if (override && !isAiTier(override)) {
        return {
            model: override,
            reasoningEffort
        };
    }
    const tier = override ?? defaultTier;
    const model = tier === 'high' ? settings.highModel : settings.lowModel;
    if (!model) return undefined;
    return {
        model,
        reasoningEffort: tier === 'high' ? settings.highReasoningEffort : settings.lowReasoningEffort
    };
}
export function resolveModelForFeature(key, settings) {
    // Through `parseAiFeatureOverride` for the same reason the app axis does it:
    // a stored value that no longer parses must fall through to the feature's
    // declared tier, not pin it to something that cannot run.
    return resolveTierModel(parseAiFeatureOverride(settings.featureOverrides?.[key]), AI_FEATURES[key].defaultTier, settings, settings.featureReasoningEfforts?.[key]);
}
/**
 * A decision-tier feature runs the default decision model unless the operator
 * pinned it to a chat model or tier, which then resolves like any override.
 */ export function resolveDecisionModelForFeature(key, settings) {
    return resolveDecisionTierModel(parseAiFeatureOverride(settings.featureOverrides?.[key]), settings, settings.featureReasoningEfforts?.[key]);
}
/**
 * `resolveTierModel` for a consumer on the decision tier, shared by features
 * and app model slots. A tier override whose model `settings` lacks falls
 * back to the decision model, as no override does.
 */ export function resolveDecisionTierModel(override, settings, reasoningEffort) {
    // With an override set, `resolveTierModel` never reads its default tier.
    const pinned = override && resolveTierModel(override, 'high', settings, reasoningEffort);
    return pinned ?? {
        model: DEFAULT_DECISION_MODEL
    };
}

//# sourceMappingURL=ai-features.js.map